import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart, Share2, Truck, Shield, RotateCcw, Star } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import ProductCard from '../components/ProductCard';
import { toast } from '../hooks/use-toast';
import { useCatalogData } from '../hooks/useCatalogData';
import { fetchProductFromCustomerView } from '../services/productService';
import { isAuthenticated } from '../lib/authUtils';

const ProductDetail = () => {
  const { slug } = useParams();
  const { products: catalogProducts, loading, error } = useCatalogData();
  // Handle both array and object with data property
  const products = useMemo(() => {
    if (Array.isArray(catalogProducts)) return catalogProducts;
    if (catalogProducts?.data && Array.isArray(catalogProducts.data)) return catalogProducts.data;
    return [];
  }, [catalogProducts]);
  
  // Transform product data to match component expectations
  const transformedProduct = useMemo(() => {
    if (!products || !products.length) return null;
    
    const productList = products;
    
    const rawProduct = 
      productList.find((p) => p.slug === slug) ||
      productList.find((p) => String(p.product_id || p.id) === slug || String(p.sku) === slug);
      console.log('rawProduct===========>', rawProduct);
    
    if (!rawProduct) return null;
    
    // Calculate total stock from stock array
    // Stock can be an array of objects with 'Stock' property, or a number
    let totalStock = 0;
    if (Array.isArray(rawProduct.stock)) {
      totalStock = rawProduct.stock.reduce((sum, stockItem) => {
        // Handle both 'Stock' (capital S) and 'stock' (lowercase) properties
        const stockValue = stockItem.Stock !== undefined 
          ? parseInt(stockItem.Stock) 
          : (stockItem.stock !== undefined ? parseInt(stockItem.stock) : 0);
        return sum + (isNaN(stockValue) ? 0 : stockValue);
      }, 0);
    } else if (typeof rawProduct.stock === 'number') {
      totalStock = rawProduct.stock;
    } else if (typeof rawProduct.stock === 'string') {
      totalStock = parseInt(rawProduct.stock) || 0;
    }
    
    // Check if promo price is valid (within date range)
    const now = new Date();
    const promoStartDate = rawProduct.promo_validity_start_date 
      ? new Date(rawProduct.promo_validity_start_date) 
      : null;
    const promoEndDate = rawProduct.promo_validity_end_date 
      ? new Date(rawProduct.promo_validity_end_date) 
      : null;
    
    const isPromoValid = rawProduct.promo_price && 
      (!promoStartDate || now >= promoStartDate) && 
      (!promoEndDate || now <= promoEndDate);
    
    // Determine display price (promo > sale > mrp)
    const displayPrice = isPromoValid && rawProduct.promo_price
      ? parseFloat(rawProduct.promo_price)
      : rawProduct.sale_price
      ? parseFloat(rawProduct.sale_price)
      : parseFloat(rawProduct.mrp_price || rawProduct.mrp || 0);
    
    const originalPrice = rawProduct.mrp_price ? parseFloat(rawProduct.mrp_price) : null;
    
    return {
      id: rawProduct.product_id || rawProduct.id,
      name: rawProduct.product_name || rawProduct.name,
      sku: rawProduct.sku,
      brand: rawProduct.brandName || rawProduct.brand,
      category: rawProduct.categoryName || rawProduct.category,
      price: displayPrice,
      originalPrice: originalPrice && originalPrice > displayPrice ? originalPrice : null,
      stock: totalStock,
      stockDetails: Array.isArray(rawProduct.stock) ? rawProduct.stock : [],
      description: rawProduct.product_desc || rawProduct.description,
      image: rawProduct.product_img || rawProduct.proudimg_Url || rawProduct.image,
      images: rawProduct.images || (rawProduct.product_img ? [rawProduct.product_img] : []),
      rating: rawProduct.rating || 4.5,
      reviews: rawProduct.reviews || 0,
      badges: rawProduct.badges || (isPromoValid ? ['sale'] : []),
      features: rawProduct.features || [],
      specifications: rawProduct.specifications || [],
      // Keep original data for cart
      ...rawProduct,
    };
  }, [products, slug]);
  
  // State for product fetched via customer-view API (used when URL contains leading productId)
  const [apiProduct, setApiProduct] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const productIdFromSlug = useMemo(() => {
    if (!slug) return null;
    const m = slug.match(/^([0-9]+)(?:-|$)/);
    return m ? m[1] : null;
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    if (!productIdFromSlug) {
      setApiProduct(null);
      setLoadingDetail(false);
      setDetailError(null);
      return;
    }

    const fetchDetail = async () => {
      setLoadingDetail(true);
      setDetailError(null);
      try {
        const fetched = await fetchProductFromCustomerView(productIdFromSlug);
        if (!mounted) return;
        if (fetched) {
          // ensure we have stockDetails for warehouse selection
          setApiProduct({ ...fetched, stockDetails: fetched.stockDetails ?? fetched.raw?.stock ?? [] });
        } else {
          // fallback to null so existing catalog search can be used
          setApiProduct(null);
          setDetailError('Product not found via customer view');
        }
      } catch (err) {
        if (!mounted) return;
        setApiProduct(null);
        setDetailError(err?.message || 'Error fetching product detail');
      } finally {
        if (mounted) setLoadingDetail(false);
      }
    };

    fetchDetail();
    return () => { mounted = false; };
  }, [productIdFromSlug]);

  const product = apiProduct || transformedProduct;
  const { addToCart, addToWishlist, isInWishlist, addToCompare } = useCart();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [activeTab, setActiveTab] = useState('description');
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  useEffect(() => {
    setSelectedImage(0);
    setQuantity(1);
    setSelectedWarehouse(null); // Reset warehouse selection when product changes
  }, [product]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    const images = product.images && product.images.length > 0 
      ? product.images 
      : [product.image || product.product_img].filter(Boolean);
    return images;
  }, [product]);
  
  // Get available warehouses from stock
  const availableWarehouses = useMemo(() => {
    if (!product) return [];
    
    // Check both stockDetails (from transformed product) and original stock array (raw stock from API)
    const stockArray = product.stockDetails || product.raw?.stock || product.stock || [];
    if (!Array.isArray(stockArray)) return [];
    
    return stockArray.filter(stock => {
      // Handle both 'Stock' (capital S) and 'stock' (lowercase) properties
      const stockValue = stock.Stock !== undefined 
        ? parseInt(stock.Stock) 
        : (stock.stock !== undefined ? parseInt(stock.stock) : 0);
      return !isNaN(stockValue) && stockValue > 0;
    });
  }, [product]);
  
  // Set default warehouse on mount or when product changes
  useEffect(() => {
    if (availableWarehouses.length > 0 && !selectedWarehouse) {
      setSelectedWarehouse(availableWarehouses[0]);
      // Set initial quantity to minimum order quantity if required
      const minQty = availableWarehouses[0].minimumOrderQuantity 
        ? parseInt(availableWarehouses[0].minimumOrderQuantity) 
        : 1;
      setQuantity(minQty);
    } else if (availableWarehouses.length === 0) {
      // Reset warehouse selection if no warehouses available
      setSelectedWarehouse(null);
    }
  }, [availableWarehouses, product]);

  const relatedProducts = useMemo(() => {
    if (!product || !products.length) return [];
    const productList = Array.isArray(products) ? products : (products.data || []);
    if (!productList.length) return [];
    
    return productList
      .filter(p => {
        const pCategory = p.categoryName || p.category;
        const pId = p.product_id || p.id;
        const productId = product.id || product.product_id;
        return pCategory === product.category && pId !== productId;
      })
      .slice(0, 4)
      .map(p => ({
        ...p,
        id: p.product_id || p.id,
        name: p.product_name || p.name,
        price: p.sale_price ? parseFloat(p.sale_price) : parseFloat(p.mrp_price || p.mrp || 0),
        image: p.product_img || p.proudimg_Url || p.image,
      }));
  }, [products, product]);

  // Quantity break pricing (bulk buy) — compute display rows and active break
  const quantityBreaks = useMemo(() => {
    if (!product) return [];

    // read flags/details from mapped product first, then fallback to raw payload
    const isQ = product.isQuantityBreak ?? product.raw?.isQuantityBreak ?? (product.data?.isQuantityBreak ?? false);
    const details = product.quantityBreakPriceDetails ?? product.raw?.quantityBreakPriceDetails ?? product.raw?.data?.quantityBreakPriceDetails ?? [];
    if (!isQ || !Array.isArray(details) || details.length === 0) return [];

    const basePrice = Number(
      product.viewPrice ?? product.price ?? product.raw?.viewPrice ?? product.raw?.viewPrice ?? product.raw?.sale_price ?? product.raw?.mrp_price ?? 0,
    );

    return details
      .map((d) => {
        const lower = Number(d.lowerRange) || 0;
        const higher = d.higherRange ? Number(d.higherRange) : null;
        let displayPrice = null;
        if (d.bulkPriceRate) {
          displayPrice = parseFloat(d.bulkPriceRate);
        } else {
          const pt = String(d.priceType ?? '').toLowerCase();
          const qbp = Number(d.QBPValue || d.qbpValue || 0);
          if (!Number.isNaN(qbp) && qbp > 0 && (pt.includes('percent') || pt.includes('precentage') || pt.includes('precent'))) {
            displayPrice = +(basePrice * (1 - qbp / 100));
          } else if (!Number.isNaN(qbp) && qbp > 0 && pt.includes('percentage')) {
            displayPrice = +(basePrice * (1 - qbp / 100));
          } else {
            displayPrice = basePrice;
          }
        }
        const price = Number.isFinite(displayPrice) ? Number(displayPrice) : null;
        const savingsPercent = basePrice > 0 && price !== null ? Math.round((1 - price / basePrice) * 100) : 0;
        return {
          lowerRange: lower,
          higherRange: higher,
          price,
          raw: d,
          savingsPercent,
        };
      })
      .filter((r) => r.price !== null)
      .sort((a, b) => a.lowerRange - b.lowerRange);
  }, [product]);

  const activeQuantityBreak = useMemo(() => {
    if (!quantityBreaks.length) return null;
    return (
      quantityBreaks.find((b) => (b.higherRange ? quantity >= b.lowerRange && quantity <= b.higherRange : quantity >= b.lowerRange)) || null
    );
  }, [quantityBreaks, quantity]);

  const displayedPrice = activeQuantityBreak ? activeQuantityBreak.price : (product?.price ?? 0);

  if (!product) {
    if (loading || loadingDetail) {
      return (
        <div className="container mx-auto px-4 py-12 text-center text-gray-500">
          Loading product details...
        </div>
      );
    }
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
        {error && <p className="text-gray-500 mb-4">Unable to load product details right now.</p>}
        <Link to="/products" className="text-red-600 hover:text-red-700">
          Back to Products
        </Link>
      </div>
    );
  }

  // relatedProducts is computed above to ensure hooks are called consistently
  // (moved here during hook-order fix)

  const handleAddToCart = () => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      toast({
        title: 'Login Required',
        description: 'Please login to add items to your cart.',
        variant: 'destructive',
      });
      navigate('/customer-login', { state: { from: { pathname: window.location.pathname } } });
      return;
    }
    
    // Check stock availability
    if (product.stock <= 0) {
      toast({
        title: 'Out of Stock',
        description: 'This product is currently out of stock.',
        variant: 'destructive',
      });
      return;
    }
    
    // Check if selected warehouse has enough stock (if warehouse is selected)
    if (selectedWarehouse) {
      const warehouseStock = selectedWarehouse.Stock !== undefined 
        ? parseInt(selectedWarehouse.Stock) 
        : (selectedWarehouse.stock !== undefined ? parseInt(selectedWarehouse.stock) : 0);
      
      if (warehouseStock < quantity) {
        toast({
          title: 'Insufficient Stock',
          description: `Only ${warehouseStock} available at ${selectedWarehouse.warehouseNames || 'selected warehouse'}.`,
          variant: 'destructive',
        });
        return;
      }
      
      // Check minimum order quantity
      if (selectedWarehouse.minimumOrderQuantity && quantity < parseInt(selectedWarehouse.minimumOrderQuantity)) {
        toast({
          title: 'Minimum Order Required',
          description: `Minimum order quantity is ${selectedWarehouse.minimumOrderQuantity} for this warehouse.`,
          variant: 'destructive',
        });
        return;
      }
    } else if (product.stock < quantity) {
      // Fallback: check total stock if no warehouse selected
      toast({
        title: 'Insufficient Stock',
        description: `Only ${product.stock} available.`,
        variant: 'destructive',
      });
      return;
    }

    // Prepare product data for cart (ensure it has required fields)
    const cartProduct = {
      ...product,
      id: product.id || product.product_id,
      price: product.price,
      name: product.name || product.product_name,
      warehouse: selectedWarehouse ? {
        name: selectedWarehouse.warehouseNames,
        id: selectedWarehouse.warehouseIds,
        stockType: selectedWarehouse.stockType,
        leadTime: selectedWarehouse.leadTime,
      } : null,
    };
    
    addToCart(cartProduct, quantity);
    toast({
      title: 'Added to cart',
      description: `${quantity}x ${product.name} added to your cart.`
    });
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={20}
        className={i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-red-600">Home</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-red-600">Products</Link>
            <span>/</span>
            <span className="text-gray-900">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Images */}
          <div>
            <div className="bg-white rounded-lg overflow-hidden mb-4 aspect-square">
              <img
                src={galleryImages[selectedImage] || product.image || '/placeholder.jpg'}
                alt={product.name}
                onError={(e) => {
                  e.target.src = '/placeholder.jpg';
                }}
                className="w-full h-full object-cover"
              />
            </div>
            {galleryImages.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square rounded overflow-hidden border-2 ${
                      selectedImage === idx ? 'border-red-600' : 'border-gray-200'
                    }`}
                  >
                    <img 
                      src={img || '/placeholder.jpg'} 
                      alt={`${product.name} ${idx + 1}`} 
                      onError={(e) => {
                        e.target.src = '/placeholder.jpg';
                      }}
                      className="w-full h-full object-cover" 
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>SKU: {product.sku}</span>
                  <span>Brand: {product.brand}</span>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                {product.badges && product.badges.map((badge, idx) => (
                  <span
                    key={idx}
                    className={`text-xs px-3 py-1 rounded font-semibold ${
                      badge === 'sale'
                        ? 'bg-red-100 text-red-600'
                        : badge === 'hot'
                        ? 'bg-orange-100 text-orange-600'
                        : 'bg-green-100 text-green-600'
                    }`}
                  >
                    {badge.toUpperCase()}
                  </span>
                ))}
              </div>
            </div>

            {/* Rating */}
            <div className="flex items-center mb-6">
              <div className="flex">{renderStars(product.rating)}</div>
              <span className="ml-2 text-gray-600">({product.reviews} reviews)</span>
            </div>

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-center space-x-4">
                <span className="text-4xl font-bold text-gray-900">${displayedPrice.toFixed(2)}</span>
                <div className="sr-only" aria-live="polite">Unit price: ${displayedPrice.toFixed(2)}</div>
                {product.originalPrice && (
                  <>
                    <span className="text-2xl text-gray-400 line-through">
                      ${product.originalPrice.toFixed(2)}
                    </span>
                    <span className="bg-red-100 text-red-600 px-3 py-1 rounded font-semibold">
                      SAVE {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%
                    </span>
                  </>
                )}
              </div>
            </div>

            {quantityBreaks.length > 0 && (
              <div className="mb-4 border-t pt-4">
                <h4 className="font-semibold text-lg mb-2">Bulk Buy & Save!</h4>
                <div className="space-y-2">
                  {quantityBreaks.map((b, idx) => {
                    const isActive = activeQuantityBreak && activeQuantityBreak.lowerRange === b.lowerRange && activeQuantityBreak.higherRange === b.higherRange;
                    const maxAvailable = selectedWarehouse ? parseInt(selectedWarehouse.Stock || 0) : (product.stock || 0);
                    const disabled = b.lowerRange > maxAvailable;
                    const label = b.higherRange ? `Buy ${b.lowerRange}-${b.higherRange}` : `Buy ${b.lowerRange}+`;
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          const minQty = selectedWarehouse?.minimumOrderQuantity ? parseInt(selectedWarehouse.minimumOrderQuantity) : 1;
                          setQuantity(Math.max(minQty, b.lowerRange));
                        }}
                        disabled={disabled}
                        className={`w-full flex items-center justify-between p-3 border rounded ${isActive ? 'bg-gray-100 border-red-600' : 'bg-white hover:bg-gray-50'}`}
                        aria-pressed={isActive}
                      >
                        <div className="text-sm text-gray-700">{label}</div>
                        <div className="text-sm font-semibold text-gray-900">
                          ${b.price.toFixed(2)} <span className="text-xs text-gray-500">{product.raw.unit_of_measurement || product.raw.unitOfMeasurement || 'each'}</span>
                          {b.savingsPercent > 0 && (
                            <span className="ml-2 text-xs text-green-600">Save {b.savingsPercent}%</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedWarehouse?.minimumOrderQuantity && (
                  <div className="text-xs text-gray-500 mt-2">min. Quantity order {selectedWarehouse.minimumOrderQuantity}</div>
                )}
              </div>
            )}

            {/* Stock */}
            <div className="mb-6">
              {product.stock > 0 ? (
                <div>
                  <div className="flex items-center text-green-600 mb-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full mr-2"></div>
                    <span className="font-semibold">In Stock ({product.stock} available)</span>
                  </div>
                  {availableWarehouses.length > 0 && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Warehouse:
                      </label>
                      <select
                        value={selectedWarehouse ? JSON.stringify(selectedWarehouse) : ''}
                        onChange={(e) => {
                          const warehouse = e.target.value ? JSON.parse(e.target.value) : null;
                          setSelectedWarehouse(warehouse);
                          setQuantity(1); // Reset quantity when warehouse changes
                        }}
                        className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                      >
                        {availableWarehouses.map((warehouse, idx) => (
                          <option key={idx} value={JSON.stringify(warehouse)}>
                            {warehouse.warehouseNames} - {warehouse.Stock} available 
                            {warehouse.leadTime && ` (${warehouse.leadTime})`}
                            {warehouse.minimumOrderQuantity && ` - Min: ${warehouse.minimumOrderQuantity}`}
                          </option>
                        ))}
                      </select>
                      {selectedWarehouse && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>Stock Type: <span className="font-semibold">{selectedWarehouse.stockType}</span></p>
                          {selectedWarehouse.leadTime && (
                            <p>Lead Time: <span className="font-semibold">{selectedWarehouse.leadTime}</span></p>
                          )}
                          {selectedWarehouse.minimumOrderQuantity && (
                            <p>Minimum Order: <span className="font-semibold">{selectedWarehouse.minimumOrderQuantity}</span></p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-red-600">
                  <div className="w-3 h-3 bg-red-600 rounded-full mr-2"></div>
                  <span className="font-semibold">Out of Stock</span>
                </div>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 mb-6">{product.description}</p>

            {/* Quantity & Actions */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex flex-col">
                <div className="flex items-center border border-gray-300 rounded">
                  <button
                    onClick={() => {
                      const minQty = selectedWarehouse?.minimumOrderQuantity 
                        ? parseInt(selectedWarehouse.minimumOrderQuantity) 
                        : 1;
                      setQuantity(Math.max(minQty, quantity - 1));
                    }}
                    className="px-4 py-2 hover:bg-gray-100"
                    disabled={selectedWarehouse && quantity <= parseInt(selectedWarehouse.minimumOrderQuantity || 1)}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={quantity}
                    min={selectedWarehouse?.minimumOrderQuantity ? parseInt(selectedWarehouse.minimumOrderQuantity) : 1}
                    max={selectedWarehouse ? parseInt(selectedWarehouse.Stock) : product.stock}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 1;
                      const minQty = selectedWarehouse?.minimumOrderQuantity 
                        ? parseInt(selectedWarehouse.minimumOrderQuantity) 
                        : 1;
                      const maxQty = selectedWarehouse 
                        ? parseInt(selectedWarehouse.Stock) 
                        : product.stock;
                      setQuantity(Math.max(minQty, Math.min(maxQty, value)));
                    }}
                    className="w-16 text-center border-x border-gray-300 py-2 focus:outline-none"
                  />
                  <button
                    onClick={() => {
                      const maxQty = selectedWarehouse 
                        ? parseInt(selectedWarehouse.Stock) 
                        : product.stock;
                      setQuantity(Math.min(maxQty, quantity + 1));
                    }}
                    className="px-4 py-2 hover:bg-gray-100"
                    disabled={selectedWarehouse && quantity >= parseInt(selectedWarehouse.Stock)}
                  >
                    +
                  </button>
                </div>
                {selectedWarehouse?.minimumOrderQuantity && (
                  <span className="text-xs text-gray-500 mt-1">
                    Minimum: {selectedWarehouse.minimumOrderQuantity}
                  </span>
                )}
              </div>
              
              <button
                onClick={handleAddToCart}
                disabled={
                  product.stock === undefined || 
                  product.stock === null || 
                  product.stock <= 0
                }
                className="flex-1 bg-red-600 text-white py-3 rounded hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold"
              >
                <ShoppingCart size={20} />
                <span>Add to Cart</span>
              </button>
              {availableWarehouses.length > 0 && !selectedWarehouse && (
                <p className="text-xs text-red-600 mt-1">
                  Please select a warehouse to add to cart
                </p>
              )}
            </div>

            {/* Secondary Actions */}
            <div className="flex space-x-4 mb-6">
              <button
                onClick={() => {
                  addToWishlist(product);
                  toast({ title: 'Added to wishlist' });
                }}
                className="flex-1 border border-gray-300 py-3 rounded hover:bg-gray-50 transition flex items-center justify-center space-x-2"
              >
                <Heart size={20} fill={isInWishlist(product.id) ? 'currentColor' : 'none'} />
                <span>Wishlist</span>
              </button>
              <button
                onClick={() => {
                  addToCompare(product);
                  toast({ title: 'Added to compare' });
                }}
                className="flex-1 border border-gray-300 py-3 rounded hover:bg-gray-50 transition flex items-center justify-center space-x-2"
              >
                <Share2 size={20} />
                <span>Compare</span>
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t pt-6">
              <div className="flex items-center space-x-3">
                <Truck className="text-red-600" size={24} />
                <div>
                  <div className="font-semibold text-sm">Free Shipping</div>
                  <div className="text-xs text-gray-600">Orders over $50</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Shield className="text-red-600" size={24} />
                <div>
                  <div className="font-semibold text-sm">Warranty</div>
                  <div className="text-xs text-gray-600">1 Year Guarantee</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <RotateCcw className="text-red-600" size={24} />
                <div>
                  <div className="font-semibold text-sm">Easy Returns</div>
                  <div className="text-xs text-gray-600">30 Day Return</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-12">
          <div className="border-b">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('description')}
                className={`py-4 font-semibold border-b-2 transition ${
                  activeTab === 'description'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Description
              </button>
              <button
                onClick={() => setActiveTab('specifications')}
                className={`py-4 font-semibold border-b-2 transition ${
                  activeTab === 'specifications'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Specifications
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`py-4 font-semibold border-b-2 transition ${
                  activeTab === 'reviews'
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Reviews ({product.reviews})
              </button>
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'description' && (
              <div>
                <h3 className="font-bold text-xl mb-4">Product Description</h3>
                <p className="text-gray-600 mb-4">{product.description}</p>
                {product.features && (
                  <>
                    <h4 className="font-semibold mb-2">Key Features:</h4>
                    <ul className="list-disc list-inside space-y-1 text-gray-600">
                      {product.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}

            {activeTab === 'specifications' && (
              <div>
                <h3 className="font-bold text-xl mb-4">Technical Specifications</h3>
                <table className="w-full">
                  <tbody>
                    {product.specifications && product.specifications.map((spec, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="py-3 px-4 font-semibold text-gray-700">{spec.label}</td>
                        <td className="py-3 px-4 text-gray-600">{spec.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h3 className="font-bold text-xl mb-4">Customer Reviews</h3>
                <div className="text-center py-8 text-gray-500">
                  <p>No reviews yet. Be the first to review this product!</p>
                  <button className="mt-4 bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition">
                    Write a Review
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductDetail;
